// ============================================================================
// TASKS GLYPHS — transcribed path-for-path from LifeOS Tasks.dc.html (option 8a).
//
// Its own module rather than an extension of components/icons.tsx, for the same boring reason
// components/school/icons.tsx gives: screens are being ported in parallel and a shared file
// edited by three lanes at once merges badly. Anything a second screen needs moves up to
// components/icons.tsx on its second use (the Rule of Three, blueprint/02).
//
// The rail's CheckSquare, the Calendar (which the Upcoming lens uses path-for-path) and the
// header Search already live in components/icons.tsx and are imported from there — a second
// copy of a glyph is the drift this file is trying to avoid.
//
// Same contract as the main set: viewBox 0 0 24 24, fill none, stroke="currentColor" so the
// colour comes from a token class, round caps and joins. A raw hex in here would fail the ship
// gate's brand audit, which is the point.
// ============================================================================

import type { IconProps } from '../icons'

function Glyph({ size = 15, strokeWidth = 1.8, className, d }: IconProps & { d: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path d={d} />
    </svg>
  )
}

/** The Inbox lens — an in-tray. */
export const Tray = (p: IconProps) => (
  <Glyph
    {...p}
    d="M4 13.4 6.2 6h11.6L20 13.4v4.2a1.4 1.4 0 0 1-1.4 1.4H5.4A1.4 1.4 0 0 1 4 17.6zM4 13.4h4.2l1 2h5.6l1-2H20"
  />
)

/**
 * The Today lens. A hair rounder than components/icons.tsx `Clock` (7.8 radius against 8, and
 * the hand runs to 13.8 rather than 14.2) — it is drawn at 15px here, not 18, and the mockup
 * redrew it for that size. Transcribed rather than substituted.
 */
export const LensClock = (p: IconProps) => (
  <Glyph {...p} d="M12 4.2a7.8 7.8 0 1 1 0 15.6 7.8 7.8 0 0 1 0-15.6M12 8.4V12l2.8 1.8" />
)

/** The Someday lens — a page with a folded corner. */
export const Page = (p: IconProps) => (
  <Glyph {...p} d="M6 4.6h9.4L19 8.2v11.2H6zM9 12.4h7M9 15.8h4.4" />
)

/** The done tick. Drawn at 1.9 in the lens column and the Done log header, 2.2 on a logged row. */
export const Tick = (p: IconProps) => <Glyph strokeWidth={1.9} {...p} d="M5 12.6l4 4L19 7.2" />

/** The Overdue group header. */
export const Warning = (p: IconProps) => (
  <Glyph
    strokeWidth={2}
    {...p}
    d="M12 8v4.6M12 16.4h.01M10.7 4.4 3.4 17.6a1.5 1.5 0 0 0 1.3 2.2h14.6a1.5 1.5 0 0 0 1.3-2.2L13.3 4.4a1.5 1.5 0 0 0-2.6 0z"
  />
)

/**
 * The row-level "this one has a reminder on it" bell. A different drawing from the Reminders
 * card's `Bell` in components/icons.tsx — this one is the 14px outline the task rows use.
 */
export const RowBell = (p: IconProps) => (
  <Glyph
    {...p}
    d="M12 4.6a5 5 0 0 0-5 5c0 4-1.6 5.4-1.6 5.4h13.2S17 13.6 17 9.6a5 5 0 0 0-5-5M10.4 18.4a1.9 1.9 0 0 0 3.2 0"
  />
)

/** Plus — the quick-add field (13px, weight 2) and the "New list" row. */
export const Plus = (p: IconProps) => <Glyph strokeWidth={2} {...p} d="M12 5.5v13M5.5 12h13" />

/**
 * The row menu's trigger — three dots. Drawn as three zero-length round-capped strokes rather
 * than three `<circle>` elements: this screen's smoke test asserts there is no `svg circle`
 * anywhere on it, because a ring is the one decoration the Tasks handoff forbids outright.
 */
export const More = (p: IconProps) => (
  <Glyph strokeWidth={2.4} {...p} d="M6 12h.01M12 12h.01M18 12h.01" />
)

/** The "4 unfiled in Inbox" strip's arrow. */
export const ArrowRight = (p: IconProps) => (
  <Glyph strokeWidth={2} {...p} d="M5 12h13M12.6 6l6 6-6 6" />
)

/**
 * RECURRENCE. Two subpaths with different cap behaviour in the mockup (the arc pair carries the
 * round join, the arrowheads are drawn open), so this one cannot go through `Glyph`.
 *
 * `heads` is false on the Done log's 10px stamp, which draws the arc alone.
 */
export function Recur({
  size = 11,
  strokeWidth = 2,
  className,
  heads = true,
}: IconProps & { heads?: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path d="M19.4 10.6a7.6 7.6 0 0 0-13-3.4L4.6 9M4.6 13.4a7.6 7.6 0 0 0 13 3.4L19.4 15" />
      {heads && <path d="M4.6 5.4v3.8h3.8M19.4 18.6v-3.8h-3.8" />}
    </svg>
  )
}
